import type {
  DadoClinicoAcao,
  EfeitoIntervencao,
  EstadoProblemaClinico,
  EstadoVitimaNaSessao,
  EstagioClinicoFicha,
  FichaVitimaAtendimento,
  IdAcaoAvaliacao,
  LeituraRegistrada,
  ProblemaClinicoFicha,
  RegiaoAnatomicaVitima,
  ResultadoEfeito,
} from './tipos'

/**
 * Motor de efeitos do atendimento: regras determinísticas preparadas na
 * ficha. Nada aqui melhora ou piora a vítima por conta própria — sem regra,
 * o efeito fica "a definir pelo instrutor".
 */

export const ESTADO_VITIMA_VAZIO: EstadoVitimaNaSessao = {
  estadoEvolucao: 'inicial',
  acoesReveladas: [],
  interpretacoes: {},
  intervencoesRealizadas: [],
  leituras: [],
  liberadasNaTela: [],
  problemas: {},
  ajustesManuais: [],
  reavaliacoesRealizadas: [],
}

const ORDEM_ESTADO: Readonly<Record<EstadoProblemaClinico, number>> = { ativo: 0, parcial: 1, controlado: 2 }

export const ROTULOS_REGIAO: Readonly<Record<RegiaoAnatomicaVitima, string>> = {
  cabeca: 'Cabeça',
  torax: 'Tórax',
  abdomen: 'Abdome',
  pelve: 'Pelve',
  dorso: 'Dorso',
  membroSuperiorDireito: 'Braço direito',
  membroSuperiorEsquerdo: 'Braço esquerdo',
  membroInferiorDireito: 'Perna direita',
  membroInferiorEsquerdo: 'Perna esquerda',
  corpoTodo: 'Corpo todo',
}

export const ROTULOS_ESTADO_PROBLEMA: Readonly<Record<EstadoProblemaClinico, string>> = {
  ativo: 'Ativo',
  parcial: 'Parcialmente controlado',
  controlado: 'Controlado',
}

export const ROTULOS_RESULTADO_EFEITO: Readonly<Record<ResultadoEfeito | 'a_definir', string>> = {
  sem_efeito: 'Sem efeito',
  parcial: 'Controle parcial',
  controlado: 'Problema controlado',
  a_definir: 'Efeito a definir pelo instrutor',
}

/**
 * Estágios da ficha além do inicial, em ordem: `estagios`, quando cadastrado,
 * ou um único estágio `tardio` sintetizado a partir de `achadosTardios`
 * (fichas antigas). Nunca os dois ao mesmo tempo — `estagios` tem prioridade.
 */
function estagiosOrdenados(ficha: FichaVitimaAtendimento): readonly EstagioClinicoFicha[] {
  if (ficha.estagios && ficha.estagios.length > 0) return ficha.estagios
  if (ficha.achadosTardios) return [{ id: 'tardio', rotulo: 'Tardio', achados: ficha.achadosTardios }]
  return []
}

/** A linha do tempo clínica da ficha para exibição: sempre começa em "Inicial". */
export function estagiosDaVitima(ficha: FichaVitimaAtendimento): ReadonlyArray<{ id: string; rotulo: string }> {
  return [{ id: 'inicial', rotulo: 'Inicial' }, ...estagiosOrdenados(ficha).map((e) => ({ id: e.id, rotulo: e.rotulo }))]
}

/** O estágio seguinte ao atual, ou `null` se já é o último (ou o id não existe na ficha). */
export function proximoEstagio(ficha: FichaVitimaAtendimento, idAtual: string): string | null {
  const ids = estagiosDaVitima(ficha).map((e) => e.id)
  const indice = ids.indexOf(idAtual)
  if (indice === -1 || indice === ids.length - 1) return null
  return ids[indice + 1]!
}

/**
 * Achado da ficha no estágio de evolução informado, sem considerar
 * problemas: aplica em cascata, a partir de `achadosIniciais`, os achados de
 * cada estágio até o alvo — o último que define o item vence.
 */
export function obterAchadoAtual(
  ficha: FichaVitimaAtendimento,
  idAcao: IdAcaoAvaliacao,
  estadoEvolucao: string,
): DadoClinicoAcao {
  const estagios = estagiosOrdenados(ficha)
  const indiceAlvo = estadoEvolucao === 'inicial' ? -1 : estagios.findIndex((e) => e.id === estadoEvolucao)

  let achado = ficha.achadosIniciais[idAcao]
  for (let i = 0; i <= indiceAlvo; i += 1) {
    const sobreposto = estagios[i]?.achados[idAcao]
    if (sobreposto) achado = sobreposto
  }
  return achado ?? { valor: 'Dado não informado ou não disponível nesta ficha', estado: 'nao_informado' }
}

export function estadoDoProblema(estado: EstadoVitimaNaSessao, problema: ProblemaClinicoFicha): EstadoProblemaClinico {
  return estado.problemas[problema.id] ?? 'ativo'
}

/**
 * O que a vítima apresenta AGORA para um exame: o achado da evolução, com
 * as sobreposições dos problemas que já mudaram de estado. É o que o
 * instrutor consulta e o que entra numa leitura nova.
 */
export function achadoAtualDaVitima(
  ficha: FichaVitimaAtendimento,
  idAcao: IdAcaoAvaliacao,
  estado: EstadoVitimaNaSessao,
): DadoClinicoAcao {
  let achado = obterAchadoAtual(ficha, idAcao, estado.estadoEvolucao)
  for (const problema of ficha.problemas ?? []) {
    const situacao = estadoDoProblema(estado, problema)
    if (situacao === 'ativo') continue
    const sobreposto = problema.achadosPorEstado?.[situacao]?.[idAcao]
    if (sobreposto) achado = sobreposto
  }
  return achado
}

/** A leitura mais recente registrada para um exame, se houver. */
export function ultimaLeitura(estado: EstadoVitimaNaSessao, idAcao: IdAcaoAvaliacao): LeituraRegistrada | null {
  for (let i = estado.leituras.length - 1; i >= 0; i -= 1) {
    if (estado.leituras[i]!.idAcao === idAcao) return estado.leituras[i]!
  }
  return null
}

export function leiturasDoExame(estado: EstadoVitimaNaSessao, idAcao: IdAcaoAvaliacao): LeituraRegistrada[] {
  return estado.leituras.filter((l) => l.idAcao === idAcao)
}

/** A vítima mudou desde a última leitura deste exame? Só o instrutor vê isso — a turma precisa reavaliar. */
export function achadoMudouDesdeUltimaLeitura(
  ficha: FichaVitimaAtendimento,
  idAcao: IdAcaoAvaliacao,
  estado: EstadoVitimaNaSessao,
): boolean {
  const leitura = ultimaLeitura(estado, idAcao)
  if (!leitura) return false
  const atual = achadoAtualDaVitima(ficha, idAcao, estado)
  return atual.valor !== leitura.achado.valor || atual.estado !== leitura.achado.estado
}

function combinar(atual: EstadoProblemaClinico, resultado: ResultadoEfeito): EstadoProblemaClinico {
  if (resultado === 'sem_efeito') return atual
  const alvo: EstadoProblemaClinico = resultado === 'controlado' ? 'controlado' : 'parcial'
  // uma intervenção posterior mais fraca não desfaz um controle já obtido
  return ORDEM_ESTADO[alvo] > ORDEM_ESTADO[atual] ? alvo : atual
}

/**
 * Aplica as regras da ficha a uma intervenção registrada. Para cada problema
 * ainda não controlado:
 *
 * - há regra para esta intervenção e ela vale na região declarada → aplica;
 * - há regra, mas a intervenção foi feita fora da região prevista → sem
 *   efeito (ex.: torniquete no braço para hemorragia na perna, ou na cabeça);
 * - não há regra, mas a intervenção foi na região do problema → "a definir
 *   pelo instrutor" (o sistema não inventa resultado);
 * - não há regra e a região é outra → não tem relação com o problema.
 */
export function avaliarIntervencao(
  ficha: FichaVitimaAtendimento,
  estado: EstadoVitimaNaSessao,
  tipoIntervencao: string,
  regiao: RegiaoAnatomicaVitima | undefined,
): EfeitoIntervencao[] {
  const efeitos: EfeitoIntervencao[] = []
  const regras = ficha.regrasEfeito ?? []

  for (const problema of ficha.problemas ?? []) {
    const antes = estadoDoProblema(estado, problema)
    if (antes === 'controlado') continue

    const regrasDoPar = regras.filter((r) => r.idProblema === problema.id && r.tipoIntervencao === tipoIntervencao)
    const aplicavel = regrasDoPar.find((r) => !r.regioes || r.regioes.length === 0 || (regiao && r.regioes.includes(regiao)))

    if (aplicavel) {
      efeitos.push({
        idProblema: problema.id,
        rotuloProblema: problema.rotulo,
        estadoAntes: antes,
        estadoDepois: combinar(antes, aplicavel.resultado),
        resultado: aplicavel.resultado,
        idRegra: aplicavel.id,
        explicacao: aplicavel.explicacao,
      })
      continue
    }

    if (regrasDoPar.length > 0) {
      const onde = regiao ? ROTULOS_REGIAO[regiao].toLowerCase() : 'local não informado'
      efeitos.push({
        idProblema: problema.id,
        rotuloProblema: problema.rotulo,
        estadoAntes: antes,
        estadoDepois: antes,
        resultado: 'sem_efeito',
        explicacao: `Aplicado em ${onde}, fora da região do problema (${ROTULOS_REGIAO[problema.regiao].toLowerCase()}).`,
      })
      continue
    }

    if (regiao && regiao === problema.regiao) {
      efeitos.push({
        idProblema: problema.id,
        rotuloProblema: problema.rotulo,
        estadoAntes: antes,
        estadoDepois: antes,
        resultado: 'a_definir',
        explicacao: 'Sem regra cadastrada para esta intervenção neste problema. Efeito a definir pelo instrutor.',
      })
    }
  }

  return efeitos
}

/** Problemas críticos que continuam sem controle — no debriefing, pendência crítica. */
export function pendenciasCriticas(
  ficha: FichaVitimaAtendimento,
  estado: EstadoVitimaNaSessao,
): Array<{ problema: ProblemaClinicoFicha; estado: EstadoProblemaClinico; intervencoesTentadas: number }> {
  return (ficha.problemas ?? [])
    .filter((p) => p.critico && estadoDoProblema(estado, p) !== 'controlado')
    .map((p) => ({
      problema: p,
      estado: estadoDoProblema(estado, p),
      intervencoesTentadas: estado.intervencoesRealizadas.filter((i) => i.efeitos?.some((e) => e.idProblema === p.id)).length,
    }))
}

/** Problemas visíveis na cena ainda não controlados, por vítima — alimenta o sangramento na cena 3D. */
export function problemasVisiveisAtivos(
  ficha: FichaVitimaAtendimento,
  estado: EstadoVitimaNaSessao,
): Exclude<EstadoProblemaClinico, 'controlado'> | null {
  let pior: Exclude<EstadoProblemaClinico, 'controlado'> | null = null
  for (const problema of ficha.problemas ?? []) {
    if (!problema.visivelNaCena) continue
    const situacao = estadoDoProblema(estado, problema)
    if (situacao === 'ativo') return 'ativo'
    if (situacao === 'parcial') pior = 'parcial'
  }
  return pior
}
